// Dialogo reusable para crear/editar un grupo de unidades (Wialon o
// Leasecontrol) — nombre + seleccion de usuarios + seleccion de unidades,
// con los mismos selectores paginados/filtrables que ya usaba
// PlataformaGruposPage.jsx (de ahi se extrajo, para poder abrir el mismo
// editor completo desde el arbol de ClientePlataformaPanel).
import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import LoadingButton from "@mui/lab/LoadingButton";
import {
  getGrupoId,
  getGrupoNombre,
  getGrupoUnitIds,
  getGrupoUsuarios,
  getUnidadId,
  getUnidadNombre,
  getUnidadUid,
  getUsuarioId,
  getUsuarioNombre,
  mergeById,
  toggleId,
} from "../utils/plataformaGrupoHelpers";

const tableHeadBg = (theme) =>
  theme.palette.mode === "dark" ? theme.palette.background.paper : theme.palette.grey[100];

// lockedIds (Set de ids como texto): filas marcadas que no se pueden cambiar (ej. usuarios de una cuenta superior que ya son miembros).
function CheckTable({
  lockedIds,
  title,
  rows = [],
  selectedIds = [],
  onToggle,
  getId,
  getPrimary,
  getSecondary,
  loading = false,
  filter = "",
  onFilterChange,
  page = 1,
  totalPages = 1,
  onPageChange,
}) {
  const selectedSet = useMemo(
    () => new Set(selectedIds.map((id) => String(id))),
    [selectedIds]
  );

  const filtered = useMemo(() => {
    const s = String(filter || "").trim().toLowerCase();
    if (!s) return rows;
    return rows.filter((row) =>
      [getPrimary?.(row), getSecondary?.(row), getId?.(row)]
        .map((v) => String(v ?? "").toLowerCase())
        .some((v) => v.includes(s))
    );
  }, [filter, rows, getId, getPrimary, getSecondary]);

  const esBloqueado = (id) => !!lockedIds && lockedIds.has(String(id));
  const visibleIds = filtered.map(getId).filter((id) => id !== undefined && id !== null && !esBloqueado(id));
  const allVisibleSelected = visibleIds.length > 0 && visibleIds.every((id) => selectedSet.has(String(id)));

  const toggleVisible = () => {
    const next = new Set(selectedSet);
    if (allVisibleSelected) {
      visibleIds.forEach((id) => next.delete(String(id)));
    } else {
      visibleIds.forEach((id) => next.add(String(id)));
    }
    onToggle?.([...next]);
  };

  return (
    <Box sx={{ border: "1px solid #e5e7eb", borderRadius: 1, minHeight: 360, overflow: "hidden" }}>
      <Box sx={{ p: 0.75, display: "flex", alignItems: "center", gap: 0.75, borderBottom: "1px solid #e5e7eb" }}>
        <Typography sx={{ fontSize: "0.78rem", fontWeight: 900, flex: 1 }}>
          {title}
        </Typography>
        <Chip size="small" label={`${selectedSet.size}/${rows.length}`} sx={{ height: 20, fontSize: "0.68rem" }} />
      </Box>
      <Box sx={{ p: 0.75, borderBottom: "1px solid #e5e7eb" }}>
        <TextField
          size="small"
          fullWidth
          placeholder="Filtrar..."
          value={filter}
          onChange={(event) => onFilterChange?.(event.target.value)}
          sx={{ "& .MuiInputBase-input": { py: 0.55, fontSize: "0.76rem" } }}
        />
      </Box>
      <TableContainer sx={{ maxHeight: 292 }}>
        <Table size="small" stickyHeader sx={{ "& .MuiTableCell-root": { py: 0.35, px: 0.6, fontSize: "0.72rem" } }}>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" sx={{ bgcolor: tableHeadBg }}>
                <Checkbox
                  size="small"
                  checked={allVisibleSelected}
                  indeterminate={!allVisibleSelected && visibleIds.some((id) => selectedSet.has(String(id)))}
                  onChange={toggleVisible}
                />
              </TableCell>
              <TableCell sx={{ bgcolor: tableHeadBg, fontWeight: 900 }}>Nombre</TableCell>
              <TableCell sx={{ bgcolor: tableHeadBg, fontWeight: 900 }}>Dato</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={3} align="center" sx={{ py: 3 }}>
                  <CircularProgress size={18} />
                </TableCell>
              </TableRow>
            ) : filtered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={3} sx={{ color: "text.secondary", fontStyle: "italic" }}>
                  Sin registros.
                </TableCell>
              </TableRow>
            ) : (
              filtered.map((row) => {
                const id = getId(row);
                const checked = selectedSet.has(String(id));
                const bloqueado = esBloqueado(id);
                return (
                  <TableRow key={String(id)} hover={!bloqueado} onClick={bloqueado ? undefined : () => onToggle?.(toggleId(selectedIds, id))} sx={{ cursor: bloqueado ? "default" : "pointer" }}>
                    <TableCell padding="checkbox">
                      <Checkbox size="small" checked={checked} disabled={bloqueado} />
                    </TableCell>
                    <TableCell sx={{ fontWeight: checked ? 900 : 600 }}>
                      {bloqueado ? (
                        <Tooltip title="Usuario de una cuenta superior: ya es parte del grupo y no se puede quitar" arrow>
                          <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
                            {getPrimary(row)}
                            <LockOutlinedIcon sx={{ fontSize: 13, color: "text.secondary" }} />
                          </Box>
                        </Tooltip>
                      ) : getPrimary(row)}
                    </TableCell>
                    <TableCell>{getSecondary(row) || "-"}</TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {onPageChange && totalPages > 1 && (
        <Box
          sx={{
            px: 0.75,
            py: 0.6,
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            gap: 0.75,
            borderTop: "1px solid #e5e7eb",
          }}
        >
          <Button
            size="small"
            disabled={loading || page <= 1}
            onClick={() => onPageChange(page - 1)}
            sx={{ minWidth: 64, fontSize: "0.7rem", py: 0.25 }}
          >
            Anterior
          </Button>
          <Typography sx={{ fontSize: "0.72rem", color: "text.secondary" }}>
            {page} / {totalPages}
          </Typography>
          <Button
            size="small"
            disabled={loading || page >= totalPages}
            onClick={() => onPageChange(page + 1)}
            sx={{ minWidth: 64, fontSize: "0.7rem", py: 0.25 }}
          >
            Siguiente
          </Button>
        </Box>
      )}
    </Box>
  );
}

const emptyForm = { nombre: "", userIds: [], unitIds: [], observacion: "" };

export default function GrupoFormDialog({
  open,
  mode = "create", // "create" | "edit"
  grupo = null, // requerido cuando mode="edit"
  onClose,
  onSaved,
  createGroup,
  updateGroup,
  listUsers,
  listUnits,
  providerColor = "#006d75",
  // Usuarios a marcar de entrada en mode="create" — ej. el usuario que ya se
  // esta viendo en el arbol cuando se aprieta "Registrar grupo", para no
  // obligar a buscarlo de nuevo en la lista.
  initialUserIds = [],
  // Solo Leasecontrol, al crear: lista las cuentas (accounts) donde puede quedar el grupo. Sin elegir una, queda en la cuenta común
  // de los usuarios del grupo o, si no hay, en la de tu sucursal. codServicioCliente pone primero las cuentas del cliente.
  listCuentas,
  codServicioCliente,
}) {
  const [form, setForm] = useState(emptyForm);
  const [cuentas, setCuentas] = useState([]);
  const [cuentaSel, setCuentaSel] = useState(null);
  const [cuentasCargando, setCuentasCargando] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  // El dialogo tiene scroll (selectores de usuarios/unidades) — un error
  // arriba del todo puede quedar fuera de vista si ya se bajo hasta Guardar,
  // asi que ademas se muestra en un Snackbar fijo abajo.
  const [snack, setSnack] = useState({ open: false, msg: "" });
  const showError = (msg) => {
    setError(msg);
    setSnack({ open: true, msg });
  };
  const [usersLoading, setUsersLoading] = useState(false);
  const [unitsLoading, setUnitsLoading] = useState(false);
  const [userOptions, setUserOptions] = useState([]);
  const [unitOptions, setUnitOptions] = useState([]);
  const [userFilter, setUserFilter] = useState("");
  const [unitFilter, setUnitFilter] = useState("");
  const [userPage, setUserPage] = useState(1);
  const [userTotalPages, setUserTotalPages] = useState(1);
  const [unitPage, setUnitPage] = useState(1);
  const [unitTotalPages, setUnitTotalPages] = useState(1);

  // Al editar, "Asignar usuarios" lista solo los usuarios de la cuenta del grupo (bact). Los miembros actuales de otras cuentas (ej. el creador)
  // siguen apareciendo marcados (se mezclan desde el propio grupo) y se conservan al guardar.
  const cuentaGrupoEdicion = mode === "edit" ? Number(grupo?.bact) || 0 : 0;
  const cuentaFiltroId = mode === "edit" ? cuentaGrupoEdicion || undefined : cuentaSel?.id;
  // Ids de todos los usuarios de la cuenta del grupo (null = aún no se sabe, o la cuenta es demasiado grande para decidirlo).
  const [idsCuentaGrupo, setIdsCuentaGrupo] = useState(null);

  // cuentaId: solo los usuarios de esa cuenta de Leasecontrol (al elegir "Cuenta" en el formulario).
  // podarSeleccion: deja marcados solo los usuarios seleccionados que pertenecen a esa cuenta.
  const loadUsers = async ({ usePage = 1, useSearch = "", cuentaId, podarSeleccion = false } = {}) => {
    if (!listUsers) return;
    setUsersLoading(true);
    try {
      const resp = await listUsers({ search: useSearch, page: usePage, limit: 50, groupBy: 0, ...(cuentaId ? { cuenta_id: cuentaId } : {}) });
      const payload = resp?.data || {};
      const data = Array.isArray(payload.data) ? payload.data : [];
      if (podarSeleccion && cuentaId) {
        const enCuenta = new Set(data.map((u) => String(getUsuarioId(u))));
        setForm((prev) => ({ ...prev, userIds: prev.userIds.filter((id) => enCuenta.has(String(id))) }));
      }
      setUserOptions(data);
      setUserPage(Number(payload.page || usePage || 1));
      setUserTotalPages(Number(payload.totalPages || 1));
    } catch (e) {
      showError(e?.response?.data?.error || e?.response?.data?.message || e.message || "No se pudieron cargar usuarios");
      setUserOptions([]);
      setUserTotalPages(1);
    } finally {
      setUsersLoading(false);
    }
  };

  const loadUnits = async ({ usePage = 1, useSearch = "" } = {}) => {
    if (!listUnits) return;
    setUnitsLoading(true);
    try {
      const resp = await listUnits({ search: useSearch, page: usePage, limit: 50 });
      const payload = resp?.data || {};
      const data = Array.isArray(payload.data) ? payload.data : [];
      setUnitOptions(data);
      setUnitPage(Number(payload.page || usePage || 1));
      setUnitTotalPages(Number(payload.totalPages || 1));
    } catch (e) {
      showError(e?.response?.data?.error || e?.response?.data?.message || e.message || "No se pudieron cargar unidades");
      setUnitOptions([]);
      setUnitTotalPages(1);
    } finally {
      setUnitsLoading(false);
    }
  };

  useEffect(() => {
    if (!open) return undefined;
    let cancelarIdsCuenta = () => {};

    setError("");
    setUserFilter("");
    setUnitFilter("");

    if (mode === "edit" && grupo) {
      const userIds = getGrupoUsuarios(grupo).map(getUsuarioId).filter(Boolean).map(String);
      const unitIds = getGrupoUnitIds(grupo).map(String);
      setForm({
        nombre: getGrupoNombre(grupo) === "-" ? "" : getGrupoNombre(grupo),
        userIds,
        unitIds,
        observacion: "",
      });
    } else {
      setForm({ ...emptyForm, userIds: initialUserIds.map(String) });
    }

    loadUsers({ usePage: 1, useSearch: "", cuentaId: mode === "edit" ? Number(grupo?.bact) || undefined : undefined });
    setIdsCuentaGrupo(null);
    if (mode === "edit" && Number(grupo?.bact) && listUsers) {
      let vivo = true;
      listUsers({ search: "", page: 1, limit: 200, groupBy: 0, cuenta_id: Number(grupo.bact) })
        .then((resp) => {
          const payload = resp?.data || {};
          const lista = Array.isArray(payload.data) ? payload.data : [];
          if (vivo && Number(payload.total || lista.length) <= 200) setIdsCuentaGrupo(new Set(lista.map((u) => String(getUsuarioId(u)))));
        })
        .catch(() => {});
      cancelarIdsCuenta = () => { vivo = false; };
    }
    loadUnits({ usePage: 1, useSearch: "" });
    return () => cancelarIdsCuenta();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mode, grupo]);

  useEffect(() => {
    if (!open || mode !== "create" || !listCuentas) {
      setCuentas([]);
      setCuentaSel(null);
      return undefined;
    }
    let vivo = true;
    setCuentaSel(null);
    setCuentasCargando(true);
    listCuentas(codServicioCliente)
      .then(({ data }) => {
        if (!vivo) return;
        const lista = (Array.isArray(data?.cuentas) ? data.cuentas : []).filter((c) => c.manager_id);
        setCuentas(lista);
        // Por defecto: la cuenta de los usuarios que el cliente ya tiene (la primera, igual que al crear un usuario). Los usuarios se
        // limitan a los de esa cuenta y se conservan marcados solo los que pertenecen a ella.
        const delCliente = lista.find((c) => c.del_cliente);
        if (delCliente) {
          setCuentaSel(delCliente);
          loadUsers({ usePage: 1, useSearch: "", cuentaId: delCliente.id, podarSeleccion: true });
        }
      })
      .catch(() => { if (vivo) setCuentas([]); })
      .finally(() => { if (vivo) setCuentasCargando(false); });
    return () => { vivo = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mode, listCuentas, codServicioCliente]);

  const handleUserFilterChange = (value) => {
    setUserFilter(value);
    loadUsers({ usePage: 1, useSearch: value, cuentaId: cuentaFiltroId });
  };

  const handleUserPageChange = (nextPage) => {
    loadUsers({ usePage: nextPage, useSearch: userFilter, cuentaId: cuentaFiltroId });
  };

  // Al elegir (o quitar) la cuenta, "Asignar usuarios" pasa a mostrar los usuarios de esa cuenta (o todos si queda en Automática).
  const elegirCuenta = (cuenta) => {
    setCuentaSel(cuenta);
    setUserFilter("");
    loadUsers({ usePage: 1, useSearch: "", cuentaId: cuenta?.id, podarSeleccion: !!cuenta });
  };

  const handleUnitFilterChange = (value) => {
    setUnitFilter(value);
    loadUnits({ usePage: 1, useSearch: value });
  };

  const handleUnitPageChange = (nextPage) => {
    loadUnits({ usePage: nextPage, useSearch: unitFilter });
  };

  const handleClose = () => {
    if (saving) return;
    onClose?.();
  };

  const handleSave = async () => {
    const nombre = form.nombre.trim();

    if (mode === "create") {
      if (!createGroup) return;
      if (!nombre) {
        showError("Debe ingresar el nombre del grupo.");
        return;
      }
      if (!form.userIds.length) {
        showError("Debe seleccionar al menos un usuario.");
        return;
      }
      setSaving(true);
      setError("");
      try {
        await createGroup({
          nombre,
          userIds: form.userIds,
          units: form.unitIds,
          observacion: form.observacion.trim(),
          ...(listCuentas ? { cod_servicio_cliente: codServicioCliente, ...(cuentaSel ? { cuenta_id: cuentaSel.id } : {}) } : {}),
        });
        onSaved?.();
      } catch (e) {
        showError(e?.response?.data?.error || e?.response?.data?.message || e.message || "No se pudo registrar el grupo");
      } finally {
        setSaving(false);
      }
      return;
    }

    if (!updateGroup || !grupo) return;
    const itemId = getGrupoId(grupo);
    if (!itemId) {
      showError("No se pudo identificar el grupo.");
      return;
    }
    if (!nombre && !form.userIds.length && !form.unitIds.length) {
      showError("Debe cambiar el nombre, usuarios o unidades.");
      return;
    }

    setSaving(true);
    setError("");
    try {
      await updateGroup(itemId, {
        nombre: nombre || undefined,
        userIds: form.userIds,
        units: form.unitIds,
        observacion: form.observacion.trim(),
      });
      onSaved?.();
    } catch (e) {
      showError(e?.response?.data?.error || e?.response?.data?.message || e.message || "No se pudo editar el grupo");
    } finally {
      setSaving(false);
    }
  };

  const usuariosBase = mode === "edit" ? getGrupoUsuarios(grupo) : [];
  // Miembros actuales del grupo que no son de su cuenta (los de cuentas superiores, p. ej. el creador): quedan marcados y bloqueados.
  const usuariosBloqueados = useMemo(
    () => (mode === "edit" && idsCuentaGrupo
      ? new Set(getGrupoUsuarios(grupo).map(getUsuarioId).filter(Boolean).map(String).filter((id) => !idsCuentaGrupo.has(id)))
      : null),
    [mode, grupo, idsCuentaGrupo]
  );
  const unidadesBase = mode === "edit" ? (grupo?.unidades_detalle || []) : [];

  return (
    <>
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ py: 1.25, fontSize: "1rem", fontWeight: 900 }}>
        {mode === "create" ? "Registrar grupo" : "Editar grupo"}
      </DialogTitle>
      <DialogContent dividers sx={{ display: "grid", gap: 1, pt: 1.25 }}>
        {!!error && (
          <Typography sx={{ fontSize: "0.8rem", color: "error.main" }}>{error}</Typography>
        )}

        <TextField
          size="small"
          label="Nombre del grupo"
          value={form.nombre}
          onChange={(event) => setForm((prev) => ({ ...prev, nombre: event.target.value }))}
          disabled={saving}
          autoFocus
          sx={{ "& .MuiInputBase-input": { py: 0.75, fontSize: "0.82rem" } }}
        />

        {mode === "create" && listCuentas && (
          <Autocomplete
            size="small"
            options={cuentas}
            value={cuentaSel}
            loading={cuentasCargando}
            disabled={saving}
            onChange={(_, c) => elegirCuenta(c)}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            getOptionLabel={(c) => c?.nombre || ""}
            groupBy={(c) => (c.del_cliente ? "Cuentas de los usuarios del cliente" : "Otras cuentas")}
            noOptionsText="No hay cuentas disponibles"
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                label="Cuenta de Leasecontrol"
                placeholder="Automática"
                helperText={cuentaSel
                  ? (cuentaSel.del_cliente
                    ? "Cuenta de los usuarios que ya tiene este cliente. Se muestran solo los usuarios de esta cuenta."
                    : "Se muestran solo los usuarios de esta cuenta.")
                  : "Automática: la cuenta común de los usuarios que asignes; si no hay, la de tu sucursal."}
              />
            )}
          />
        )}

        {mode === "edit" && listUsers && !!cuentaGrupoEdicion && (
          <Typography variant="caption" color="text.secondary">
            Solo se muestran los usuarios de la cuenta de este grupo. Los miembros actuales de otras cuentas (por ejemplo, quien lo creó) se conservan.
          </Typography>
        )}

        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 1 }}>
          {listUsers && (
            <CheckTable
              title="Asignar usuarios"
              lockedIds={usuariosBloqueados}
              rows={mergeById(usuariosBase, userOptions, getUsuarioId)}
              selectedIds={form.userIds}
              onToggle={(ids) => setForm((prev) => ({ ...prev, userIds: ids }))}
              getId={getUsuarioId}
              getPrimary={getUsuarioNombre}
              getSecondary={(u) => getUsuarioId(u)}
              loading={usersLoading}
              filter={userFilter}
              onFilterChange={handleUserFilterChange}
              page={userPage}
              totalPages={userTotalPages}
              onPageChange={handleUserPageChange}
            />
          )}

          {listUnits && (
            <CheckTable
              title="Asignar unidades"
              rows={mergeById(unidadesBase, unitOptions, getUnidadId)}
              selectedIds={form.unitIds}
              onToggle={(ids) => setForm((prev) => ({ ...prev, unitIds: ids }))}
              getId={getUnidadId}
              getPrimary={getUnidadNombre}
              getSecondary={getUnidadUid}
              loading={unitsLoading}
              filter={unitFilter}
              onFilterChange={handleUnitFilterChange}
              page={unitPage}
              totalPages={unitTotalPages}
              onPageChange={handleUnitPageChange}
            />
          )}
        </Box>

        <TextField
          size="small"
          label="Observacion"
          value={form.observacion}
          onChange={(event) => setForm((prev) => ({ ...prev, observacion: event.target.value }))}
          disabled={saving}
          multiline
          minRows={2}
          sx={{ "& .MuiInputBase-input": { fontSize: "0.82rem" } }}
        />
      </DialogContent>
      <DialogActions sx={{ px: 2, py: 1 }}>
        <Button size="small" onClick={handleClose} disabled={saving}>
          Cancelar
        </Button>
        <LoadingButton
          size="small"
          variant="contained"
          loading={saving}
          disabled={usersLoading || unitsLoading}
          onClick={handleSave}
          sx={{ bgcolor: providerColor, "&:hover": { bgcolor: providerColor } }}
        >
          Guardar
        </LoadingButton>
      </DialogActions>
    </Dialog>

    <Snackbar
      open={snack.open}
      autoHideDuration={5000}
      onClose={() => setSnack((s) => ({ ...s, open: false }))}
      anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
    >
      <Alert
        severity="error"
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        sx={{ width: "100%" }}
      >
        {snack.msg}
      </Alert>
    </Snackbar>
    </>
  );
}
